import * as React from "react"

type ExpandOptions = {
  initExpanded?: boolean
  minHeight?: number
}

type ExpandProps<T> = {
  containerRef?: React.RefObject<T>
  options?: ExpandOptions
}

/**
 *  This hook is used to expand a container when the content is larger than the min height.
 * @param containerRef The ref of the container to expand
 * @param options
 * @returns
 */
export function useExpand<T extends HTMLElement = HTMLDivElement>({
  containerRef,
  options: { initExpanded = false, minHeight = 250 } = {},
}: ExpandProps<T> = {}) {
  const internalRef = React.useRef<T | null>(null)
  const ref = containerRef || internalRef

  const [isExpanded, setIsExpanded] = React.useState(initExpanded)
  const [isAboveMinHeight, setIsAboveMinHeight] = React.useState(false)

  React.useEffect(() => {
    if (ref.current) {
      setIsAboveMinHeight(ref.current.scrollHeight > minHeight)
    }
  }, [ref, minHeight])

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev)
  }

  return {
    ref,
    isExpanded,
    isAboveMinHeight,
    toggleExpanded,
  }
}
